/**
 * PlanRevisionView — what the plan actually proposes, in full (R3 `TaskSnapshot.plan`).
 *
 * This is the surface the read-model spec was written to unblock: today the only rendering of a plan
 * is the ask's one-line prompt ("Approve plan version 1 (2 items)?"), which cannot show an approver
 * that one of those two items is an IRREVERSIBLE delete. Every item here renders its `op`,
 * `irreversible` flag, `depends_on` and `manual_prerequisites` — nothing an approver needs is behind
 * a click they have to know to make (except the coverage unit list and, past ~50 items in a group,
 * the rest of that group — see below).
 *
 * "NOT INCLUDED" (`out_of_scope`) gets its own section with a plain-English reason per item —
 * `unsupported_type` reads "not something automation can build yet", not the bare enum token — because
 * an approver reading only the included items would not otherwise learn the plan quietly dropped
 * something they asked for.
 *
 * SCALES TO 5000 ITEMS: items are grouped by `op`, and any group past `DEFAULT_VISIBLE_ROWS` renders
 * only its first slice plus a count — `views.ts`'s `visibleSlice` decides that slice, so the component
 * never maps over more than ~50 rows per group on first render regardless of how large the plan is.
 * "Show all" is a `<details>` disclosure (native, keyboard-reachable), and expanding it is the only
 * path that renders the rest — a reader who never opens it never pays for those rows.
 */
import { useState } from "react";
import { AlertTriangle, HelpCircle, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PlanView } from "@/services/multi-agent/laneV2/types";
import { PlanItemDetail } from "./PlanItemDetail";
import {
  coverageVerdictCounts,
  coverageVerdictLabel,
  groupByOp,
  groupFindingsByKind,
  opLabel,
  orgFindingKindLabel,
  orgFindingSummary,
  outOfScopeReasonLabel,
  shortId,
  shortSha,
  visibleSlice,
} from "./views";

export interface PlanRevisionViewProps {
  plan: PlanView;
  /** The plan is background once the changes are proposed: fold it behind a "Plan (vN)" summary. */
  collapsed?: boolean;
  className?: string;
}

export function PlanRevisionView({ plan, collapsed = false, className }: PlanRevisionViewProps) {
  if (!collapsed) return <PlanCard plan={plan} className={className} />;
  return (
    <details data-lane-v2-plan-collapsed="" className={cn("rounded-md border border-border bg-card", className)}>
      <summary className="cursor-pointer px-3.5 py-2.5 text-[13px] font-semibold text-foreground">
        Plan <span className="font-normal tabular-nums text-muted-foreground">(v{plan.version})</span>
      </summary>
      <PlanCard plan={plan} className="rounded-none border-0 border-t" />
    </details>
  );
}

function PlanCard({ plan, className }: { plan: PlanView; className?: string }) {
  const itemsById = new Map(plan.items.map((it) => [it.item_id, it]));
  const groups = groupByOp(plan.items);
  const coverage = plan.coverage;
  const notices = (plan.plan_notes ?? []).filter((n) => n.level === "notice");
  // Anything that is not "notice" is a diagnostic: a level a later MSA adds must not surface on top.
  const details = (plan.plan_notes ?? []).filter((n) => n.level !== "notice");
  const verdictCounts = coverage?.result === "checked" ? coverageVerdictCounts(coverage.units) : [];

  return (
    <section aria-labelledby="lanev2-plan-title" className={cn("rounded-md border border-border bg-card", className)}>
      <header className="px-3.5 py-3">
        <h3 id="lanev2-plan-title" className="text-[14px] font-semibold text-foreground">
          Plan
          <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">v{plan.version}</span>
        </h3>
        <details data-lane-v2-plan-technical="" className="mt-0.5">
          <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">Technical details</summary>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            <span title={plan.plan_revision_id} className="font-mono">
              rev {shortId(plan.plan_revision_id)}
            </span>
            <span title={plan.content_sha256} className="font-mono">
              sha {shortSha(plan.content_sha256)}
            </span>
          </p>
        </details>
        {plan.goal ? <p className="mt-1.5 text-[13px] leading-snug text-foreground">{plan.goal}</p> : null}
      </header>

      <div className="border-t border-border">
        {groups.map((group) => (
          <PlanItemGroup key={group.op} op={group.op} items={group.items} itemsById={itemsById} />
        ))}
      </div>

      {(plan.assumptions?.length ?? 0) > 0 || (plan.open_questions?.length ?? 0) > 0 ? (
        <div className="grid gap-3 border-t border-border px-3.5 py-3 sm:grid-cols-2">
          {plan.open_questions?.length ? (
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Questions still open</h4>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] leading-snug text-foreground">
                {plan.open_questions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {plan.assumptions?.length ? (
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Assumptions</h4>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] leading-snug text-foreground">
                {plan.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {notices.length > 0 && (
        <div data-testid="lanev2-plan-notices" className="border-t border-border px-3.5 py-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Notes</h4>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] leading-snug text-foreground">
            {notices.map((n, i) => (
              <li key={i}>{n.text}</li>
            ))}
          </ul>
        </div>
      )}

      {plan.out_of_scope && plan.out_of_scope.length > 0 && (
        <div data-testid="lanev2-not-included" className="border-t border-border px-3.5 py-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Not included ({plan.out_of_scope.length})
          </h4>
          <ul className="mt-1.5 space-y-1.5">
            {plan.out_of_scope.map((oos, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[13px] leading-snug text-foreground">
                <HelpCircle className="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0">
                  <span>{oos.what}</span>
                  {(oos.metadata_type || oos.full_name) && (
                    <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                      {oos.metadata_type ?? ""}
                      {oos.metadata_type && oos.full_name ? " · " : ""}
                      {oos.full_name ?? ""}
                    </span>
                  )}
                  <span className="block text-[12px] text-muted-foreground">{outOfScopeReasonLabel(oos.reason)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {typeof plan.org_findings_checked === "boolean" && (
        <div data-testid="lanev2-org-findings" className="border-t border-border px-3.5 py-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            What was found in your org
            {plan.org_findings_checked && (plan.org_findings?.length ?? 0) > 0
              ? ` (${plan.org_findings_total ?? plan.org_findings!.length})`
              : ""}
          </h4>
          {!plan.org_findings_checked ? (
            <p className="mt-1 text-[12px] text-muted-foreground">Not checked against the org yet.</p>
          ) : (plan.org_findings?.length ?? 0) === 0 ? (
            <p className="mt-1 text-[12px] text-muted-foreground">No findings.</p>
          ) : (
            <div className="mt-1.5 space-y-2">
              {groupFindingsByKind(plan.org_findings!).map((g) => (
                <div key={g.op}>
                  <p className="text-[11px] font-medium capitalize text-muted-foreground">
                    {orgFindingKindLabel(g.op)} <span className="font-normal normal-case text-muted-foreground/80">({g.items.length})</span>
                  </p>
                  <ul className="mt-0.5 space-y-0.5">
                    {g.items.map((f, i) => (
                      <li key={i} className="text-[12px] leading-snug text-foreground">
                        {orgFindingSummary(f)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-border px-3.5 py-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Does it cover your request?</h4>
        {!coverage || !coverage.result ? (
          <p className="mt-1 text-[12px] text-muted-foreground">Not recorded for this plan revision.</p>
        ) : coverage.result === "unchecked" ? (
          <p className="mt-1 text-[12px] text-muted-foreground">
            This plan was not checked against the request{coverage.error ? ` (${coverage.error})` : ""}.
          </p>
        ) : coverage.result === "checked" ? (
          <div className="mt-1">
            <div className="flex flex-wrap gap-1.5">
              {verdictCounts.map(({ verdict, count }) => (
                <span key={verdict} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {count} {coverageVerdictLabel(verdict)}
                </span>
              ))}
            </div>
            {coverage.context_incomplete && (
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-ma-warn-text">
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                The model did not have the full context when it checked this.
              </p>
            )}
            {coverage.units && coverage.units.length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
                  {coverage.units.length} coverage {coverage.units.length === 1 ? "unit" : "units"}
                </summary>
                <ul className="mt-1 space-y-1">
                  {coverage.units.map((u, i) => (
                    <li key={i} className="text-[12px] leading-snug text-foreground">
                      <span className="font-medium">{coverageVerdictLabel(u.verdict)}</span> — {u.unit}
                      {u.reason ? <span className="block text-[11px] text-muted-foreground">{u.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : (
          <p className="mt-1 text-[12px] text-muted-foreground">{coverage.result}</p>
        )}
      </div>

      {details.length > 0 && (
        <details data-testid="lanev2-plan-details" className="border-t border-border px-3.5 py-3">
          <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
            Details for reviewers ({details.length})
          </summary>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[12px] leading-snug text-muted-foreground">
            {details.map((n, i) => (
              <li key={i}>{n.text}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function PlanItemGroup({
  op,
  items,
  itemsById,
}: {
  op: string;
  items: PlanView["items"];
  itemsById: Map<string, PlanView["items"][number]>;
}) {
  // A real toggle (`useState`), not a native `<details>`: `<details>` still mounts its closed
  // content into the markup (a browser only hides it with CSS), which on a 5000-item plan is 5000
  // rows the tab pays to build and lay out whether or not anyone ever opens it. Gating the render
  // itself on `expanded` is what actually keeps the collapsed group cheap.
  const [expanded, setExpanded] = useState(false);
  const { visible, hiddenCount } = visibleSlice(items);
  const shown = expanded ? items : visible;
  return (
    <div className="border-b border-border/60 px-3.5 py-2.5 last:border-b-0">
      <h4 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
        {opLabel(op)} <span className="font-normal normal-case text-muted-foreground/80">({items.length})</span>
      </h4>
      <ul className="mt-1.5 space-y-2">
        {shown.map((item) => (
          <PlanItemRow key={item.item_id} item={item} itemsById={itemsById} />
        ))}
      </ul>
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          Show {hiddenCount} more
        </button>
      )}
    </div>
  );
}

function PlanItemRow({
  item,
  itemsById,
}: {
  item: PlanView["items"][number];
  itemsById: Map<string, PlanView["items"][number]>;
}) {
  return (
    <li className="text-[13px] leading-snug">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[12px] text-foreground">
          {item.metadata_type} · {item.full_name}
        </span>
        {item.irreversible && (
          <span className="inline-flex items-center gap-1 rounded-full border border-ma-err-line px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ma-err-text">
            <ShieldAlert className="h-3 w-3 shrink-0" aria-hidden />
            Can't be undone
          </span>
        )}
      </div>
      {item.rationale && <p className="mt-0.5 text-[12px] text-muted-foreground">{item.rationale}</p>}
      {item.depends_on && item.depends_on.length > 0 && (
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Depends on: {item.depends_on.map((id) => itemsById.get(id)?.full_name ?? id).join(", ")}
        </p>
      )}
      {item.manual_prerequisites && item.manual_prerequisites.length > 0 && (
        <p className="mt-0.5 text-[11px] text-ma-warn-text">Needs first: {item.manual_prerequisites.join(", ")}</p>
      )}
      <PlanItemDetail item={item} />
    </li>
  );
}
